/**
 * C5: browser notifications for job completion.
 *
 * Thin wrapper over the Notification API. Permission is requested lazily the
 * first time a job finishes while the page is open; if the user has not
 * granted it (or the browser lacks support) every call is a safe no-op, so
 * the job page works exactly as before.
 */

export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

export function notificationsEnabled(): boolean {
  return notificationsSupported() && Notification.permission === 'granted'
}

/** Ask for permission (must be called from a user gesture to be honoured). */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!notificationsSupported()) return 'denied'
  if (Notification.permission !== 'default') return Notification.permission
  try {
    return await Notification.requestPermission()
  } catch {
    return Notification.permission
  }
}

/**
 * Notify that a job reached a terminal state. Requests permission first if
 * still "default". Returns true when a notification was actually shown.
 */
export async function notifyJobFinished(
  title: string,
  body: string,
  onClickUrl?: string
): Promise<boolean> {
  if (!notificationsSupported()) return false
  const perm = await requestNotificationPermission()
  if (perm !== 'granted') return false
  try {
    const n = new Notification(title, { body, icon: '/favicon.ico', tag: 'mpcfill-job' })
    if (onClickUrl) {
      n.onclick = () => {
        window.focus()
        window.location.href = onClickUrl
      }
    }
    return true
  } catch {
    return false
  }
}
